import React from 'react'
import { Stack } from '@hub/stack'
import { setCacheHeaders } from '@scentregroup/shared/helpers/set-cache-headers'
import Layout from '@scentregroup/shared/hub-components/layout'
import {
  NationalStaticData,
  fetchNationalStaticData,
} from '@scentregroup/shared/hub-components/universal-header'
import { localeToCountry } from '@scentregroup/shared/helpers/local-to-country'
import { makeApolloClient } from '../../lib/apolloClient'
import { GetServerSideProps, GetServerSidePropsContext } from '../../types/next'
import {
  ITopStripProps,
  getTopStripProps,
} from '@scentregroup/shared/hub-components/top-strip'
import { useGetHrefContext } from '@scentregroup/shared/context'
import { getEnvBool } from '@scentregroup/shared/config'
import { CoreContainer } from '@hub/core-container'
import { LinkPagination } from '@scentregroup/shared/components/pagination'
import { useRouter } from '@scentregroup/shared/next/router'
import { getAbsolutePathFromRouter } from '@scentregroup/shared/helpers/url-helpers'
import { InferGetServerSidePropsType } from 'next'
import {
  ContentPageData,
  getContentPage,
} from '../../lib/get-content-detail/content-page'
import { ViewControl } from '@scentregroup/shared/hub-components/view-control'
import { PaginationSummary } from '@scentregroup/shared/hub-components/pagination-summary'
import {
  CategoriesNav,
  CentreSelectorNav,
  CentreSelectorTrigger,
  fetchPopularCategories,
  fetchRetailCategoryHeirarchy,
  INationalStoreDirectory,
  makeNationalStoreDirectoryFromSgRetailersSearchResponse,
  NationalStoreDirectory,
  Pills,
  PopularCategoriesReel,
  PopularCategory,
  RetailCategory,
  StoresBreadcrumb,
  StoresBreadcrumbPath,
  SubCategoriesReel,
  makeMetaData,
} from '../../components/pages/stores'
import { H1, H2 } from '@hub/heading'
import ActionSheetContainer from '../../components/pages/stores/action-sheet-container'
import {
  FilterFormData,
  getFormdataFromQuery,
} from '../../components/pages/stores/types'
import { isEntryDefinedAndNotNull } from '@scentregroup/shared/lib'
import { Box } from '@hub/box'
import {
  GetNationalStoreDirectoryDocument,
  SgRetailersSearchOrder,
} from '@scentregroup/shared/graphql'
import { useStoresRoute } from '../../components/pages/stores/use-stores-route'
import { Text } from '@hub/text'

const PAGE_LIMIT = 24

const paramToArray = (param: string | string[] | undefined): string[] =>
  Array.isArray(param) ? param : param === undefined ? [] : [param]

export const getServerSideProps: GetServerSideProps<
  StoresIndexPageProps
> = async ({ locale, res, query, params }: GetServerSidePropsContext) => {
  // N.B. this is a server-side only feature flag so we get it directly
  // from process.env, see MKP-7744:
  if (!getEnvBool(process.env.FEAT_FLAG_ENABLE_STORE_DIRECTORY)) {
    setCacheHeaders(res, 'no-cache')
    return {
      redirect: {
        destination: '/',
        permanent: false,
      },
    }
  }

  const country = localeToCountry(locale)
  const client = makeApolloClient()
  const formData: FilterFormData = getFormdataFromQuery(query)

  const page = Math.max(0, Number(query.page ?? 1) - 1)
  const limit = PAGE_LIMIT
  const skip = page * limit

  const [nationalHeaderData, storesContent, topStripProps, categories] =
    await Promise.all([
      fetchNationalStaticData(client, { country }),
      getContentPage(client, country, 'stores'),
      getTopStripProps(client, country),
      fetchRetailCategoryHeirarchy(client, country),
    ])

  const [selectedCategories] = paramToArray(params?.categories).reduce(
    ([p, parent]: [RetailCategory[], RetailCategory[] | undefined], slug) => {
      const item = parent?.find(cat => cat.slug === slug)
      if (!item) {
        return [p, undefined]
      }
      return [p.concat(item), item.children]
    },
    [[], categories]
  )

  const isEmptyCategories =
    paramToArray(params?.categories).length !== selectedCategories.length
  const emptyCategoriesData = {
    data: {
      sgRetailersSearch: { total: 0, items: [] },
    },
  }

  const { data } = isEmptyCategories
    ? emptyCategoriesData
    : await client.query({
        query: GetNationalStoreDirectoryDocument,
        context: { version: 2 },
        variables: {
          limit,
          skip,
          filter: {
            country,
            retailCategoryIds:
              selectedCategories.length > 0
                ? selectedCategories.map(item => item.id).slice(-1)
                : null,
          },
          order:
            formData.sortBy === 'desc'
              ? SgRetailersSearchOrder.TitleDesc
              : SgRetailersSearchOrder.TitleAsc,
        },
      })

  if (!nationalHeaderData || !storesContent || !data?.sgRetailersSearch) {
    setCacheHeaders(res, 'no-cache')
    return {
      notFound: true,
    }
  }

  const popularCategories = await fetchPopularCategories(client, storesContent)
  const [topCategory, subCategory] = params?.categories || []
  const selectedCategoryId =
    popularCategories?.find(category => category.slug === topCategory)?.id ||
    null
  const subCategories =
    categories.find(cat => cat.id === selectedCategoryId)?.children || null
  const selectedSubCategoryId =
    subCategories?.find(category => category.slug === subCategory)?.id || null

  const nationalStoreDirectory =
    makeNationalStoreDirectoryFromSgRetailersSearchResponse(
      data.sgRetailersSearch
    )

  const categoryPath = selectedCategories.map(category => ({
    title: category.title,
    slug: category.slug,
  }))

  const metaData = makeMetaData(storesContent, selectedCategories)
  setCacheHeaders(res)
  return {
    props: {
      country,
      topStripProps,
      nationalHeaderData,
      nationalStoreDirectory,
      page,
      limit,
      storesContent: { ...storesContent, metaData },
      popularCategories,
      selectedCategoryId,
      subCategories,
      selectedSubCategoryId,
      categoryPath,
      categories,
      formData,
    },
  }
}

export interface StoresIndexPageProps {
  country: ReturnType<typeof localeToCountry>
  nationalHeaderData: NationalStaticData
  topStripProps: ITopStripProps | null
  nationalStoreDirectory: INationalStoreDirectory
  page: number
  limit: number
  storesContent: ContentPageData
  popularCategories: PopularCategory[] | null
  selectedCategoryId: string | null
  subCategories: RetailCategory[] | null
  selectedSubCategoryId: string | null
  categoryPath: StoresBreadcrumbPath
  categories: RetailCategory[]
  formData: FilterFormData
}

const StoresIndexPage: React.FC<
  React.PropsWithChildren<
    InferGetServerSidePropsType<typeof getServerSideProps>
  >
> = ({
  country,
  topStripProps,
  nationalHeaderData,
  nationalStoreDirectory,
  page,
  limit,
  storesContent,
  popularCategories,
  selectedCategoryId,
  subCategories,
  selectedSubCategoryId,
  categoryPath,
  categories,
  formData,
}: StoresIndexPageProps) => {
  const router = useRouter()
  const href = useGetHrefContext()()
  const canonical = React.useMemo(
    () => new URL(new URL(href).pathname, href).toString(),
    [href]
  )

  const viewMode = formData.viewMode

  const handleFormData = React.useCallback(
    (formData: FilterFormData): void => {
      const params = Object.entries(formData).map<[string, string | undefined]>(
        ([key, value]) => {
          if (key === 'viewMode' && value === 'auto') {
            return [key, undefined]
          }

          if (key === 'sortBy' && value === 'asc') {
            return [key, undefined]
          }

          return [key, value]
        }
      )

      const overrideParams = {
        ...Object.fromEntries(
          params.filter(([_, value]) => value === undefined)
        ),
      }

      const extraParams = Object.fromEntries(
        params.filter(isEntryDefinedAndNotNull)
      )

      router.push(
        getAbsolutePathFromRouter(router, overrideParams, extraParams)
      )
    },
    [router]
  )

  const makePageLink = React.useCallback(
    (page: number): string => {
      const queryOverride = page === 0 ? { page: undefined } : undefined
      const queryExtra = page === 0 ? undefined : { page: String(page + 1) }
      return getAbsolutePathFromRouter(router, queryOverride, queryExtra)
    },
    [router]
  )

  const selectedCategory = React.useMemo(
    () => popularCategories?.find(({ id }) => id == selectedCategoryId),
    [popularCategories, selectedCategoryId]
  )
  const selectedCategorySlug = selectedCategory?.slug

  const linkToCategory = useStoresRoute()

  const {
    metaData: { title, shortDescription },
  } = storesContent
  const metaData = {
    title: title,
    description: shortDescription,
    canonical,
  }

  const [openCategoriesNav, setOpenCategoriesNav] = React.useState(false)
  const [openCentreSelectorNav, setOpenCentreSelectorNav] =
    React.useState(false)

  const isStoreDirectoryEmpty = nationalStoreDirectory.items.length === 0

  return (
    <>
      <Layout
        country={country}
        topStrip={topStripProps}
        header={{
          hideMegaMenu: true,
          variant: 'national',
          data: nationalHeaderData,
          logoText: null,
        }}
        metaData={{ ...metaData }}
      >
        <StoresBreadcrumb path={categoryPath} />
        <CoreContainer>
          <Stack
            gap={['spacing-lg', null, 'spacing-3xl']}
            shouldWrapChildren={false}
          >
            <Stack
              gap={['spacing-xs', null, 'spacing-sm']}
              shouldWrapChildren={false}
            >
              <H1>{title}</H1>
              <CentreSelectorTrigger
                country={country}
                onClick={() => setOpenCentreSelectorNav(true)}
              />
            </Stack>
            {popularCategories && (
              <Box>
                <H2 align="center">Popular categories</H2>
                <PopularCategoriesReel
                  categories={popularCategories}
                  selectedCategoryId={selectedCategoryId}
                  onClickMoreCategories={() => setOpenCategoriesNav(true)}
                />
                {subCategories && selectedCategorySlug && (
                  <SubCategoriesReel
                    items={[
                      {
                        key: 'all',
                        title: 'All',
                        link: linkToCategory([selectedCategorySlug]),
                        isSelected: !selectedSubCategoryId,
                      },
                    ].concat(
                      subCategories.map(cat => ({
                        key: cat.id,
                        title: cat.title,
                        link: linkToCategory(
                          cat.id === selectedSubCategoryId
                            ? [selectedCategorySlug]
                            : [selectedCategorySlug, cat.slug]
                        ),
                        isSelected: cat.id === selectedSubCategoryId,
                      }))
                    )}
                  />
                )}
              </Box>
            )}
            <Stack
              gap={['spacing-md', null, 'spacing-lg']}
              shouldWrapChildren={false}
            >
              <Stack
                gap={['spacing-md', null, 'spacing-sm']}
                shouldWrapChildren={false}
              >
                <Stack
                  direction={['column', null, 'row']}
                  shouldWrapChildren={false}
                  align={['left', null, 'center']}
                  sx={{
                    rowGap: 'spacing-lg',
                    width: [null, null, '100%'],
                  }}
                >
                  <PaginationSummary
                    total={nationalStoreDirectory.total}
                    limit={limit}
                    page={page}
                    suffix={` results for ${country}`}
                    sx={{
                      flexGrow: 1,
                      order: [1, null, 0],
                    }}
                  />
                  <ActionSheetContainer
                    data={formData}
                    onChange={handleFormData}
                  />
                  <ViewControl
                    value={viewMode}
                    onChange={viewMode =>
                      handleFormData({ ...formData, viewMode })
                    }
                    sx={{
                      flexGrow: 0,
                      display: ['none', null, 'flex'],
                    }}
                  />
                </Stack>
                <Pills path={categoryPath} />
              </Stack>
              {!isStoreDirectoryEmpty && (
                <NationalStoreDirectory
                  items={nationalStoreDirectory.items}
                  view={viewMode}
                />
              )}
            </Stack>
            {isStoreDirectoryEmpty ? (
              <Box sx={{ marginTop: 'spacing-md', textAlign: 'center' }}>
                <Text
                  fontSize="font-2xl"
                  tone="strong"
                  sx={{
                    marginBottom: 'spacing-sm',
                  }}
                >
                  No matching stores
                </Text>
                <Text>Try changing categories</Text>
              </Box>
            ) : (
              <Stack sx={{ alignItems: ['center', 'center', 'end'] }}>
                <LinkPagination
                  currentPage={page}
                  totalPages={Math.ceil(nationalStoreDirectory.total / limit)}
                  makePageLink={makePageLink}
                />
              </Stack>
            )}
          </Stack>
        </CoreContainer>
      </Layout>
      <CategoriesNav
        categories={categories}
        open={openCategoriesNav}
        onClose={() => setOpenCategoriesNav(false)}
      />
      <CentreSelectorNav
        centres={nationalHeaderData.centres}
        country={country}
        open={openCentreSelectorNav}
        onClose={() => setOpenCentreSelectorNav(false)}
      />
    </>
  )
}

export default StoresIndexPage
